import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, MapPin, Plane, MessageCircle, Phone, CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { INTERNATIONAL_DESTINATIONS, INTERNATIONAL_FAQS, COMPANY, PACKAGES } from '../mock';
import { PageHero } from './DestinationsPages';
import { Button } from '../components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import EnquiryForm from '../components/sections/EnquiryForm';
import { CTABanner } from '../components/sections/CTASections';
import { PackageCard } from '../components/sections/Packages';
import HeroSection from '../components/sections/HeroSection';
import ReviewCards from '../components/sections/ReviewCards';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';

export function InternationalDestinationsPage() {
  return (
    <div>
      <Helmet>
        <title>International Tour Packages | Mountains Chain Travel</title>
        <meta name="description" content="Explore amazing international tour packages with Mountains Chain Travel. Dubai, Thailand, Maldives, Singapore & more. Fully customizable tours with visa assistance." />
        <meta property="og:title" content="International Tour Packages | Mountains Chain Travel" />
        <meta property="og:description" content="Explore amazing international tour packages with Mountains Chain Travel. Dubai, Thailand, Maldives, Singapore & more. Fully customizable tours with visa assistance." />
      </Helmet>

      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img src={INTERNATIONAL_DESTINATIONS[0].image} alt="International Destinations" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-transparent to-brand-blue/30" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-orange rounded-full text-white text-[13px] font-bold mb-6 shadow-lg shadow-brand-orange/40">
              <span className="tracking-wide">Visa Assistance · Customizable Tours</span>
            </div>
            <h1 className="font-poppins text-[48px] sm:text-[56px] lg:text-[72px] text-white leading-[1.05] font-extrabold drop-shadow-2xl">
              <span className="text-brand-orange">International</span>
              <br />
              <span>Destinations</span>
            </h1>
            <p className="text-white text-[18px] lg:text-[21px] mt-5 max-w-xl leading-relaxed font-medium drop-shadow-lg">
              Explore the world with India's #1 DMC. Dubai, Thailand, Maldives, Singapore & more - fully customizable tours.
            </p>
            <div className="mt-6 inline-flex flex-wrap items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              <span className="bg-brand-orange text-white text-[12px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Popular</span>
              <span className="text-white text-[14px] lg:text-[15px] font-semibold">
                Dubai · Thailand · Maldives · Singapore · Bali · Vietnam
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Review Cards - Right after hero */}
      <ReviewCards />
      <CustomerVideoReviews />

      {/* Filter pills */}
      <section className="max-w-7xl mx-auto px-6 pt-10">
        <div className="flex flex-wrap gap-2 mb-6">
          <Link to="/destinations" className="px-4 py-2 rounded-full text-[13px] font-semibold bg-brand-grey text-brand-blue hover:bg-brand-greyDark transition-colors">All Destinations</Link>
          <Link to="/destinations" className="px-4 py-2 rounded-full text-[13px] font-semibold bg-brand-grey text-brand-blue hover:bg-brand-greyDark transition-colors">Domestic</Link>
          <span className="px-4 py-2 rounded-full text-[13px] font-semibold bg-brand-blue text-white">International</span>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">9 Countries</div>
            <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Explore the world with <span className="text-brand-orange">India's #1 DMC</span></h2>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[13px] text-brand-blue/65"><Plane size={16} className="text-brand-orange"/>Visa assistance included</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INTERNATIONAL_DESTINATIONS.map((d, i) => (
            <Link key={d.slug} to={`/international/${d.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-brand-greyDark shadow-card card-hover img-zoom-wrap relative h-[340px] animate-fade-up" style={{animationDelay: `${i*70}ms`}}>
              <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue via-brand-blue/40 to-transparent" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md text-[11px] font-bold text-brand-blue">
                <Plane size={11} className="text-brand-orange" />{d.country}
              </div>
              <div className="absolute top-4 right-4 bg-brand-orange text-white text-[11px] font-bold px-2.5 py-1 rounded-md">From ₹{d.startingPrice.toLocaleString('en-IN')}</div>
              <div className="relative h-full p-5 flex flex-col justify-end text-white">
                <h3 className="font-poppins font-bold text-[26px] leading-tight capitalize">{d.name}</h3>
                <p className="text-white/85 text-[13.5px] mt-1.5 line-clamp-2">{d.tagline}</p>
                <div className="mt-3 flex items-center gap-2 text-brand-orange font-semibold text-[13px] group-hover:gap-3 transition-all">
                  Explore {d.name} Tours <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trust banner */}
      <section className="bg-brand-grey py-12">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {icon: Plane, t: 'Visa Assistance', d: 'End-to-end support'},
            {icon: Sparkles, t: 'Hand-picked Hotels', d: 'Verified 3–5 star'},
            {icon: CheckCircle2, t: 'Best Price Promise', d: 'We beat any quote'},
            {icon: Clock, t: '24x7 Support', d: 'Even while abroad'},
          ].map((x, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 flex items-center gap-4 border border-brand-greyDark">
              <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center shrink-0"><x.icon size={22} className="text-brand-orange"/></div>
              <div><div className="font-poppins font-semibold text-brand-blue text-[15px]">{x.t}</div><div className="text-[12.5px] text-brand-blue/60">{x.d}</div></div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner />

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16 lg:py-20">
        <div className="text-center mb-8">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">FAQ</div>
          <h2 className="font-poppins text-[30px] lg:text-[40px] text-brand-blue leading-[1.1]">Frequently Asked <span className="text-brand-orange">Questions</span></h2>
          <p className="text-brand-blue/65 mt-3 text-[14.5px]">Everything you need to know about booking international tours with us.</p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {INTERNATIONAL_FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-brand-greyDark">
              <AccordionTrigger className="text-left text-brand-blue font-semibold text-[15px] lg:text-[16px] hover:text-brand-orange hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-brand-blue/70 text-[14.5px] leading-relaxed pb-5 whitespace-pre-line">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <EnquiryForm />
    </div>
  );
}

export function InternationalDestinationDetailPage() {
  const { slug } = useParams();
  const dest = INTERNATIONAL_DESTINATIONS.find(d => d.slug === slug);
  if (!dest) return (
    <div className="max-w-7xl mx-auto px-6 py-20 text-center text-brand-blue">
      Destination not found. <Link to="/international" className="text-brand-orange underline">Back to international destinations</Link>
    </div>
  );
  const similar = INTERNATIONAL_DESTINATIONS.filter(d => d.slug !== slug).slice(0, 4);
  const samplePkgs = PACKAGES.slice(0, 3);

  return (
    <div>
      <Helmet>
        <title>{dest.name} Tour Packages | Mountains Chain Travel</title>
        <meta name="description" content={`Experience the best ${dest.name} tour packages. ${dest.tagline} End-to-end support and visa assistance included.`} />
        <meta property="og:title" content={`${dest.name} Tour Packages | Mountains Chain Travel`} />
        <meta property="og:description" content={`Experience the best ${dest.name} tour packages. ${dest.tagline} End-to-end support and visa assistance included.`} />
      </Helmet>
      <HeroSection slug={slug} name={dest.name} startingPrice={dest.startingPrice} image={dest.image} />
      <CustomerVideoReviews />
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-orange/10 text-brand-orange rounded-full text-[12px] font-bold uppercase tracking-wider">
              <Plane size={13}/> {dest.country} · International
            </div>
            <h2 className="font-poppins text-[28px] text-brand-blue mt-4 mb-3 capitalize flex items-center gap-2"><Sparkles size={22} className="text-brand-orange"/>About {dest.name}</h2>
            <p className="text-brand-blue/75 text-[15px] leading-relaxed">From iconic landmarks to hidden gems, our {dest.name} tours are crafted by our destination specialists who have personally explored every corner. Every package is fully customizable to your dates, group size, hotel preference and budget. We handle visa documentation, airport pickups, sightseeing transfers and 24x7 on-trip support.</p>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-3">Top experiences in {dest.name}</h3>
            <div className="flex flex-wrap gap-2">
              {dest.highlights.map(h => (
                <span key={h} className="px-3 py-1.5 bg-brand-grey rounded-full text-[13px] text-brand-blue font-medium flex items-center gap-1.5">
                  <MapPin size={12} className="text-brand-orange"/>{h}
                </span>
              ))}
            </div>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-4">What's included</h3>
            <ul className="space-y-2.5">
              {['Round-trip flights (optional)', `${dest.nights} nights hotel stay (3–5 star options)`, 'Airport transfers & sightseeing', 'Daily breakfast & select meals', 'Visa documentation assistance', 'Local English-speaking guide', 'Travel insurance support', '24x7 on-trip WhatsApp helpline'].map((x, i) => (
                <li key={i} className="flex items-start gap-3 text-brand-blue/80 text-[14.5px]">
                  <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0"/>{x}
                </li>
              ))}
            </ul>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-10 mb-4">Why book {dest.name} with Mountains Chain</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                {t: 'Visa Handled', d: 'We process your visa documentation end-to-end'},
                {t: 'Best Price', d: 'Direct deals with global hotel chains'},
                {t: 'Verified Stays', d: 'We have personally inspected every hotel'},
                {t: '24x7 Help', d: 'Real humans on call even abroad'},
              ].map((x, i) => (
                <div key={i} className="bg-white border border-brand-greyDark rounded-xl p-4 card-hover">
                  <div className="font-poppins font-semibold text-brand-blue text-[14.5px]">{x.t}</div>
                  <div className="text-brand-blue/65 text-[12.5px] mt-1">{x.d}</div>
                </div>
              ))}
            </div>
          </div>

          <aside className="bg-brand-grey rounded-2xl p-6 h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2 text-brand-orange text-[11px] font-bold uppercase tracking-wider mb-3"><Clock size={14}/>Plan Your Trip</div>
            <p className="text-brand-blue/70 text-[13.5px] mb-4">Get a custom itinerary in minutes. Free, no obligation - a real travel designer replies on WhatsApp.</p>
            <div className="flex flex-col gap-2.5 mt-2">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m planning a ' + dest.name + ' trip and would love a customized international tour package.\n\nPlease share:\n- Available tour options\n- Visa assistance details\n- Hotel & flight options\n\nThank you!')}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-11 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>Get Custom Quote</Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="w-full border-brand-blue/20 text-brand-blue hover:bg-brand-blue hover:text-white h-11 rounded-xl"><Phone size={16} className="mr-2"/>Call to Customize</Button>
              </a>
            </div>
            <div className="mt-5 pt-5 border-t border-brand-greyDark space-y-2.5 text-[12.5px] text-brand-blue/75">
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Free cancellation up to 14 days</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Pay just 20% to book</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Visa assistance included</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>0% EMI available</div>
            </div>
          </aside>
        </div>

        {/* Similar */}
        <div className="mt-16">
          <h2 className="font-poppins text-[26px] text-brand-blue mb-6">Other international destinations</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {similar.map(s => (
              <Link key={s.slug} to={`/international/${s.slug}`} className="group rounded-xl overflow-hidden img-zoom-wrap relative h-44">
                <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover img-zoom" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-3 text-white">
                  <div className="text-[10.5px] text-white/75 uppercase tracking-wider">{s.country}</div>
                  <h4 className="font-poppins font-bold text-[16px] capitalize leading-tight">{s.name}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <EnquiryForm />
    </div>
  );
}
